import { DigitalWalletOwnerInfoResponse, TransactionRowResult } from "@/models/transaction";
import { formatDateTime, formatAmount } from "@/util";
import { Grid, List, ListItem } from "@mui/material";
import React, { Dispatch, FC, SetStateAction, useCallback, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import employeeBreadcrumbStore from "@/stores/employeeBreadcrumbStore";

interface ITransactionsGrid {
    sortBy: Function;
    transactions: TransactionRowResult[];
    isSearch: boolean;
    fetchMore: Function;
    hasNext: boolean;
    orderBy: string;
    asc: boolean;
    info: DigitalWalletOwnerInfoResponse;
    setSelectedTransaction: Dispatch<SetStateAction<TransactionRowResult | null>>;
}

const TransactionsGrid: FC<ITransactionsGrid> = (props: ITransactionsGrid) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const gridEl = useRef() as React.MutableRefObject<HTMLInputElement>;
    const navigate = useNavigate();

    const openEmployeePage = (id: number | undefined, employeeName: string) => {
        if (!id) {
            return;
        }
        employeeBreadcrumbStore.update(employeeName);
        navigate("/employees/" + id + "/info");
    };

    const handleSelectedTransaction = (transaction: TransactionRowResult) => {
        props.setSelectedTransaction(transaction);
    };

    /* infinite scroll */
    const scrollListener = useCallback(() => {
        let bottom = gridEl.current.scrollHeight - gridEl.current.clientHeight;
        if (gridEl.current.scrollTop > 0 && gridEl.current.scrollTop >= bottom && props.hasNext) {
            props.fetchMore();
        }
    }, [gridEl, props]);
    useLayoutEffect(() => {
        const gridRef = gridEl.current;
        gridRef.addEventListener("scroll", scrollListener);
        return () => {
            gridRef.removeEventListener("scroll", scrollListener);
        };
    }, [scrollListener]);
    /* infinite scroll */

    function getArrowClass(sort: string) {
        if (sort === props.orderBy) {
            if (props.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }
    return (
        <Grid
            sx={{
                width: "100%",
                position: "relative",
            }}
        >
            <Grid className="transaction-search-grid" ref={gridEl}>
                {props.transactions.length > 0 && (
                    <List className="transaction-search-list">
                        <ListItem className="transaction-search-list-header">
                            <Grid className="transaction-search-grid-header">
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionNumber")}
                                </Grid>
                                <Grid
                                    item
                                    width="70%"
                                    onClick={() => props.sortBy("TransactionType")}
                                    className="sortable"
                                >
                                    <span>
                                        {t("transaction.TransactionType")}
                                        <i className={`icon-f ${getArrowClass("TransactionType")} fs-12`} />
                                    </span>
                                </Grid>
                                <Grid
                                    item
                                    width="70%"
                                    onClick={() => props.sortBy("TransactionDate")}
                                    className="sortable text-align-right"
                                >
                                    <span>
                                        {t("transaction.TransactionDate")}
                                        <i className={`icon-f ${getArrowClass("TransactionDate")} fs-12`} />
                                    </span>
                                </Grid>
                                <Grid item width="70%" onClick={() => props.sortBy("User")} className="sortable">
                                    <span>
                                        {t("interfaces.user")}
                                        <i className={`icon-f ${getArrowClass("User")} fs-12`} />
                                    </span>
                                </Grid>
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionAmount")}
                                </Grid>
                                <Grid item width="70%" className="text-align-right">
                                    {t("transaction.TransactionConnectedOrder")}
                                </Grid>
                            </Grid>
                        </ListItem>
                        {props.transactions.map((row) => (
                            <ListItem
                                className="transaction-search-list-item"
                                key={row.id}
                                onClick={() => handleSelectedTransaction(row)}
                            >
                                <Grid className="transaction-search-grid-body">
                                    <Grid item width="70%" className="text-align-right">
                                        {row.id}
                                    </Grid>
                                    <Grid item width="90%">
                                        {t(`transaction.${row.transactionType}`)}
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        {formatDateTime(row.transactionDate)}
                                    </Grid>
                                    <Grid item width="70%">
                                        {row.username}
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        <div className={`transaction-amount ${row.transactionType}`}>
                                            {formatAmount(row.transactionAmount, row.currencyId)}
                                        </div>
                                    </Grid>
                                    <Grid item width="70%" className="text-align-right">
                                        {row.connectedOrderId != null ? (
                                            <div
                                                className="link-element table-number-format-value"
                                                onClick={() =>
                                                    navigate(
                                                        `/digital-wallet/list/${id}/transactions/${row.connectedOrderId}`,
                                                        { state: { docketNumber: row.docket } }
                                                    )
                                                }
                                            >
                                                {row.docket}
                                            </div>
                                        ) : (
                                            ""
                                        )}
                                    </Grid>
                                </Grid>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>
        </Grid>
    );
};

export default TransactionsGrid;
